import React, { useCallback, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { confirmDialog } from 'primereact/confirmdialog';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { MetasColaboradorService } from '../services/metasColaborador.service';
import type { AlcanceAsignadoVista, AsignarAlcanceRequest, ListaAlcances } from '../types/metas.types';
import { instanteMx } from '../utils/formato';
import { AsignarAlcanceForm } from './AsignarAlcanceForm';
import { AlertasAlcance, etiquetaAlcance } from './AccesosAvisos';
import { GRUPO_CONFIRMAR } from '../utils/confirmar';
import { CLASE_OVERLAY } from './ui/clases';

type Aviso = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  onAviso?: Aviso;
}

/**
 * Accesos a metas: quién es supervisor o regional de quién, y quién tiene el de
 * dirección —todas las tiendas en sólo lectura, desde el 21-oct-2026— (pestaña
 * «Accesos», sólo administrador; la misma asignación sigue también en Usuarios).
 *
 * Se llamaba «Alcances»; ese nombre pasa a la venta real contra la meta, como
 * en el diseño. La tabla `metas_alcance_usuario` y la ruta de la API no cambian.
 *
 * ⚠️ Los nombres del catálogo son LLAVES DE ACCESO: renombrar a un supervisor
 * en el catálogo deja a su cuenta sin tiendas. Por eso esta vista pone a la
 * vista los dos huérfanos: alcances cuyo nombre ya no existe (en rojo) y
 * nombres del catálogo que nadie tiene asignados («Sin cuenta asignada»).
 */
export const AccesosPanel: React.FC<Props> = ({ onAviso }) => {
  const esMovil = useEsMovil(760);
  const [datos, setDatos] = useState<ListaAlcances | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setDatos(await MetasColaboradorService.listarAlcances());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const asignar = async (req: AsignarAlcanceRequest) => {
    setOcupado(true);
    try {
      const r = await MetasColaboradorService.asignarAlcance(req);
      onAviso?.('success', 'Alcance asignado', `${r.email}: ${etiquetaAlcance(r)}.`);
      await cargar();
    } catch (e) {
      onAviso?.('error', 'No se pudo asignar', (e as Error).message);
    } finally {
      setOcupado(false);
    }
  };

  const quitar = (a: AlcanceAsignadoVista) =>
    confirmDialog({
      group: GRUPO_CONFIRMAR,
      header: 'Quitar alcance',
      message:
        a.tipo === 'direccion'
          ? `${a.email} deja de ver todas las tiendas (dirección). No cambia nada de lo capturado ni de lo autorizado.`
          : `${a.email} deja de ser ${etiquetaAlcance(a).toLowerCase()}. Lo que ya capturó o autorizó se conserva.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, quitar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await MetasColaboradorService.revocarAlcance(a.id);
          onAviso?.('success', 'Alcance retirado', `${a.email}: ${etiquetaAlcance(a)}.`);
          await cargar();
        } catch (e) {
          onAviso?.('error', 'No se pudo quitar', (e as Error).message);
        }
      },
    });

  const cuenta = (a: AlcanceAsignadoVista) => (
    <div className="flex flex-col">
      <span className="font-medium">{a.email}</span>
      {a.nombreUsuario && <span className="text-xs text-gray-500">{a.nombreUsuario}</span>}
    </div>
  );
  const botonQuitar = (a: AlcanceAsignadoVista) => (
    <Button
      label="Quitar"
      icon="pi pi-trash"
      size="small"
      severity="danger"
      outlined
      onClick={() => quitar(a)}
      aria-label={`Quitar ${etiquetaAlcance(a)} a ${a.email}`}
    />
  );

  const alcances = datos?.alcances ?? [];

  return (
    <div className="flex flex-col gap-6" data-testid="accesos-panel">
      {error && (
        <div className="flex flex-wrap items-center gap-2">
          <Message severity="error" text={`No se pudieron leer los alcances: ${error}`} />
          <Button label="Reintentar" icon="pi pi-refresh" size="small" onClick={() => void cargar()} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section className="lg:col-span-4">
          <h3 className="mb-2 text-lg font-semibold text-gray-800">Asignar</h3>
          <p className="mb-3 text-sm text-gray-500">
            Sólo a cuentas con rol Indicadores: conservan su tablero y ganan el acceso a metas de sus tiendas.
          </p>
          {datos && (
            <AsignarAlcanceForm
              opciones={datos.opciones}
              cuentas={datos.cuentas}
              ocupado={ocupado}
              onAsignar={asignar}
              claseOverlay={CLASE_OVERLAY}
            />
          )}
        </section>

        <section className="lg:col-span-8">
          <h3 className="mb-2 text-lg font-semibold text-gray-800">
            Alcances vigentes {datos && <Tag value={String(alcances.length)} severity="info" />}
          </h3>
          {esMovil ? (
            <ul className="flex flex-col gap-2">
              {alcances.map((a) => (
                <li key={a.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  {cuenta(a)}
                  <div className="mt-1 text-sm">{etiquetaAlcance(a)}</div>
                  <AlertasAlcance a={a} />
                  <div className="mt-1 text-xs text-gray-500">
                    {a.asignadoPor} · {instanteMx(a.asignadoEn)}
                  </div>
                  <div className="mt-2">{botonQuitar(a)}</div>
                </li>
              ))}
              {!alcances.length && !cargando && <li className="text-sm text-gray-500">Nadie tiene alcance todavía.</li>}
            </ul>
          ) : (
            <DataTable value={alcances} dataKey="id" size="small" loading={cargando} emptyMessage="Nadie tiene alcance todavía">
              <Column header="Cuenta" body={cuenta} />
              <Column
                header="Alcance"
                body={(a: AlcanceAsignadoVista) => (
                  <div className="flex flex-col gap-1">
                    <span>{etiquetaAlcance(a)}</span>
                    <AlertasAlcance a={a} />
                  </div>
                )}
              />
              <Column
                header="Asignado"
                body={(a: AlcanceAsignadoVista) => (
                  <div className="text-xs text-gray-600">
                    <div>{a.asignadoPor}</div>
                    <div>{instanteMx(a.asignadoEn)}</div>
                  </div>
                )}
                style={{ width: 200 }}
              />
              <Column body={botonQuitar} style={{ width: 120 }} />
            </DataTable>
          )}
        </section>
      </div>

      {datos && (
        <section>
          <h3 className="mb-2 text-lg font-semibold text-gray-800">Sin cuenta asignada</h3>
          <p className="mb-3 text-sm text-gray-500">
            Nombres del catálogo de tiendas que nadie tiene asignados: sus tiendas no tienen quién capture o
            autorice (salvo un administrador).
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {(['supervisores', 'regionales'] as const).map((k) => (
              <div key={k}>
                <div className="mb-1 font-semibold capitalize">{k}</div>
                <div className="flex flex-wrap gap-1">
                  {datos.sinCuenta[k].map((n) => (
                    <Tag key={n} value={n} severity="warning" />
                  ))}
                  {!datos.sinCuenta[k].length && <span className="text-sm text-green-700">Todos tienen cuenta.</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AccesosPanel;
